import { ThemeProvider } from "./theme/ThemeContext";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { PropsWithChildren, useContext, useEffect } from "react";
import { FeatureFlagsProvider } from "./app-settings/FeatureFlagsContext";
import { BrandContext, BrandProvider } from "./brand/BrandContext";
import { AppSettingsProvider } from "./app-settings/AppSettingsContext";
import { UserProvider } from "./model/user/UserContext";
import { ProgressBarProvider } from "./layout/sidebar/progress-bar/context/ProgressBarContext";
import ScrollToTop from "./utils/ScrollToTop";
import { AppInitializerProvider } from "./initializers/AppInitializerContext";
import MainPage from "./pages/main-page/MainPage";
import { ChakraProvider } from "@chakra-ui/react";
import { useFunnelTheme } from "./v2/modules/branding/useFunnelTheme";
import { Outlet } from "react-router-dom";
import { Fonts } from "@airhelp/react";
import { CacheProvider } from "@emotion/react";
import createCache, { Options } from "@emotion/cache";
import rtl from "stylis-plugin-rtl";
import { getLocaleDirection, LocaleDirection } from "utils/getLocaleDirection";
import { useAppSettingsSnapshot } from "v2/settings";
import { LocaleDirectionProvider } from "app-settings/LocaleDirectionProvider";
import { useBrandSnapshot } from "v2/modules/branding";
import { GrowthBookProvider } from "@growthbook/growthbook-react";
import { loadGrowthbook } from "tracking/tools/growthbook";

export const defaultQueryClient = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: false },
  },
});

export const ApplicationProviders = ({ children }: PropsWithChildren) => {
  const { brand } = useContext(BrandContext);
  const theme = useFunnelTheme(brand);
  const { locale } = useAppSettingsSnapshot();
  const growthbook = loadGrowthbook(locale);

  useEffect(() => {
    growthbook.init({ streaming: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ChakraProvider theme={theme}>
      <Fonts />
      <ThemeProvider>
        <QueryClientProvider client={defaultQueryClient}>
          <FeatureFlagsProvider>
            <GrowthBookProvider growthbook={growthbook}>
              <AppSettingsProvider>
                <LocaleDirectionProvider>
                  <UserProvider>
                    <ProgressBarProvider>{children}</ProgressBarProvider>
                  </UserProvider>
                </LocaleDirectionProvider>
              </AppSettingsProvider>
            </GrowthBookProvider>
          </FeatureFlagsProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </ChakraProvider>
  );
};

export function ApplicationShell() {
  return (
    <BrandProvider>
      <ApplicationProviders>
        <ScrollToTop />
        <AppInitializerProvider>
          <MainPage>
            <Outlet />
          </MainPage>
        </AppInitializerProvider>
      </ApplicationProviders>
    </BrandProvider>
  );
}

export function ApplicationShellV2({ children }: PropsWithChildren) {
  const brand = useBrandSnapshot();
  const funnelTheme = useFunnelTheme(brand);
  const { locale } = useAppSettingsSnapshot();
  const localeDirection = getLocaleDirection(locale);
  const options: Record<LocaleDirection, Options> = {
    rtl: { key: "css-rtl", stylisPlugins: [rtl] },
    ltr: { key: "css", stylisPlugins: [] },
  };
  const cache = createCache(options[localeDirection]);
  const growthbook = loadGrowthbook(locale);

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = localeDirection;
    growthbook.init({ streaming: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  return (
    <GrowthBookProvider growthbook={growthbook}>
      <ChakraProvider theme={funnelTheme}>
        <CacheProvider value={cache}>
          <Fonts />
          <QueryClientProvider client={defaultQueryClient}>{children}</QueryClientProvider>
        </CacheProvider>
      </ChakraProvider>
    </GrowthBookProvider>
  );
}
